import { Component, OnDestroy, OnInit, ViewEncapsulation, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { NgIf } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { HttpParams } from '@angular/common/http';
import moment from 'moment-timezone';
import { combineLatest, interval } from 'rxjs';

import { FuseNavigationItem, FuseNavigationService, FuseVerticalNavigationComponent } from '../../../../../../shared/src/lib/@fuse/components/navigation';
import { FuseMediaWatcherService } from '../../../../../../shared/src/lib/@fuse/services/media-watcher';
import { LoadingBarOnPushComponent } from '../../../../../../shared/src/lib/feature-fuse/loading-bar-onpush/loading-bar-onpush.component';
import { FeatureFuseModule } from '../../../../../../shared/src/lib/feature-fuse/feature-fuse.module';
import { User } from '../../../../../../shared/src/lib/model/user.model';
import { Formation } from '../../../../../../shared/src/lib/model/formation.model';
import { selectCurrentUser } from '../../../../../../shared/src/lib/store/selectors/user.selectors';
import { selectSelectedFormation } from '../../../../../../shared/src/lib/store/selectors/formation.selectors';
import { selectAllGroups } from '../../../../../../shared/src/lib/store/selectors/group.selectors';
import { selectAllRoutePermissions } from '../../../../../../shared/src/lib/store/selectors/routePermission.selectors';
import { OccurrenceService } from '../../../../../../shared/src/lib/service/occurrence.service';
import { RateStudentService } from '../../../../../../shared/src/lib/service/rate-student.service';
import { ConversationService } from '../../../../../../shared/src/lib/service/conversation.service';
import { AuthenticationService } from '../../../../../../shared/src/lib/service/authentification.service';
import { UserComponent } from '../../common/user/user.component';
import { FormationSwitcherComponent } from '../../common/formation-switcher/formation-switcher.component';
import { NextSessionModalComponent } from '../../../../../../shared/src/lib/feature-fuse/next-session-modal-fuse/next-session-modal.component';
import { AchievementModalFuseComponent } from '../../../../../../shared/src/lib/feature-fuse/achievement-modal-fuse/achievement-modal-fuse.component';
import { getStudentNavigation } from '../../../core/navigation/navigation.data';
import { Student } from '../../../../../../shared/src/lib/model/student.model';

@Component({
    selector: 'classy-layout',
    templateUrl: './classy.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        LoadingBarOnPushComponent, 
        FuseVerticalNavigationComponent, 
        NgIf, 
        MatIconModule, 
        MatButtonModule, 
        RouterOutlet, 
        UserComponent, 
        FeatureFuseModule, 
        TranslateModule,
        FormationSwitcherComponent,
        NextSessionModalComponent,
        AchievementModalFuseComponent
    ],
})
export class ClassyLayoutComponent implements OnInit, OnDestroy {
    isScreenSmall: boolean;
    navigation: FuseNavigationItem[];
    student: Student;
    currentFormation: Formation;
    hasPastOccurrences: boolean = false;
    hasReportExist: boolean = false;  // ✅ État du rapport d'évaluation
    nbMessagesUnread: number = 0;

    private destroyRef = inject(DestroyRef);
    private store = inject(Store);
    private occurrenceService = inject(OccurrenceService);
    private rateStudentService = inject(RateStudentService);
    private conversationService = inject(ConversationService);
    private authenticationService = inject(AuthenticationService);

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _fuseNavigationService: FuseNavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private translateService: TranslateService
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to user and current formation from store
        combineLatest([
            this.store.select(selectCurrentUser),
            this.store.select(selectSelectedFormation)
        ])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(([student, formation]) => {
                this.student = student;
                this.currentFormation = formation;

                if (formation) {
                    // Générer la navigation dynamique basée sur la formation
                    this.navigation = this.getFilteredNavigation(formation, this.hasPastOccurrences, this.hasReportExist);

                    // Charger les occurrences passées
                    this.loadPastOccurrences(formation.id);

                    // Charger l'état du rapport d'évaluation
                    this.checkIfReportExist(formation.id);

                    // Charger le nombre de messages non lus
                    if (this.student) {
                        this.loadUnreadMessagesCount(formation, this.student);
                    }
                }
            });

        // Polling toutes les 10 secondes pour rafraîchir le nombre de messages non lus
        interval(30000)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                if (this.currentFormation && this.student) {
                    this.loadUnreadMessagesCount(this.currentFormation, this.student);
                }
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Cleanup handled by takeUntilDestroyed
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    /**
     * Update navigation badge dynamically
     * 
     * @param itemId - ID de l'item de navigation (ex: 'nav-messages')
     * @param count - Nombre à afficher dans le badge
     */
    private updateNavigationBadge(itemId: string, count: number): void {
        // Récupérer le composant de navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>('mainNavigation');
        
        if (!navigation) return;

        // Mettre à jour le badge via l'API Fuse
        if (count > 0) {
            navigation.navigation.forEach(item => {
                if (item.id === itemId) {
                    item.badge = {
                        title: count.toString(),
                        classes: 'px-2 bg-primary text-white rounded-full'
                    };
                }
            });
        } else {
            // Supprimer le badge si count = 0
            navigation.navigation.forEach(item => {
                if (item.id === itemId && item.badge) {
                    delete item.badge;
                }
            });
        }

        // Forcer le refresh du composant
        navigation.refresh();
    }

    /**
     * Générer la navigation filtrée selon les permissions
     * Comme dans lingueopro header-menu avec checkFeaturePermission et checkAvailableLanguageSheets
     */
    private getFilteredNavigation(formation: Formation, hasPastOccurrences: boolean, hasReportExist: boolean = false): FuseNavigationItem[] {
        const allNavItems = getStudentNavigation(formation, hasPastOccurrences, hasReportExist);

        return allNavItems.filter(item => {
            // Filtrer les sheets selon les permissions
            if (item.id === 'nav-sheets') {
                const hasPermission = this.authenticationService.checkFeaturePermission('sheets');
                const hasLanguage = this.authenticationService.checkAvailableLanguageSheets(formation.language_detail.id);
                return hasPermission && hasLanguage;
            }

            // Filtrer les exercices selon les permissions
            if (item.id === 'nav-exercices') {
                const hasPermission = this.authenticationService.checkFeaturePermission('sheets');
                const hasLanguage = this.authenticationService.checkAvailableLanguageSheets(formation.language_detail.id);
                return hasPermission && hasLanguage;
            }

            // Garder tous les autres items
            return true;
        });
    }

    /**
     * Charger les occurrences passées pour déterminer si le menu "Cours passés" doit être activé
     * Comme dans lingueopro header-menu.component.ts getPastOccurrences()
     */
    private loadPastOccurrences(formationId: number): void {
        let params = new HttpParams();
        params = params.append('formation', formationId.toString());
        params = params.append('start__lte', moment().toISOString());
        params = params.append('ordering', '-start');
        params = params.append('page_size', '1');

        this.occurrenceService.getObservableOccurences(params)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(res => {
                if (res && res['results'] && res['results'].length > 0) {
                    this.hasPastOccurrences = true;
                    // Régénérer la navigation avec le flag hasPastOccurrences mis à jour
                    if (this.currentFormation) {
                        this.navigation = this.getFilteredNavigation(
                            this.currentFormation, 
                            this.hasPastOccurrences, 
                            this.hasReportExist
                        );
                    }
                } else {
                    this.hasPastOccurrences = false;
                }
            });
    }

    /**
     * Charger le nombre de messages non lus
     * Comme dans lingueopro header-menu.component.ts getNumberMessagesUnread()
     */
    private loadUnreadMessagesCount(formation: Formation, student: Student): void {
        if (!formation.teachers || formation.teachers.length === 0) return;

        const teacherIds = formation.teachers.map(t => t.id).join(',');
        let params = new HttpParams();
        params = params.append('teacher', teacherIds);
        params = params.append('student', student.id.toString());

        this.conversationService.getConversations(params)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(conversations => {
                if (conversations && conversations['results']) {
                    this.conversationService.conversationList.next(conversations['results']);
                    
                    this.nbMessagesUnread = 0;
                    conversations['results'].forEach((conv: any) => {
                        this.nbMessagesUnread += conv['unread_student'] || 0;
                    });

                    // Mettre à jour le badge des messages
                    this.updateNavigationBadge('nav-messages', this.nbMessagesUnread);
                }
            });
    }

    /**
     * Vérifie si un rapport d'évaluation existe pour la formation
     * Même logique que student-level-widget-fuse
     */
    private checkIfReportExist(formationId: number): void {
        let params = new HttpParams();
        params = params.append('session__formation', formationId.toString());
        params = params.append('page_size', '1');
        
        this.rateStudentService.getRatingStudents(params)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    this.hasReportExist = res['count'] > 0;
                    // Régénérer la navigation avec hasReportExist mis à jour
                    if (this.currentFormation) {
                        this.navigation = this.getFilteredNavigation(
                            this.currentFormation, 
                            this.hasPastOccurrences, 
                            this.hasReportExist
                        );
                    }
                },
                error: () => {
                    this.hasReportExist = false;
                }
            });
    }
}
